import type { WidgetSize } from './BaseWidget';

export const COLUMNS = 12;
export const COLUMN_STEP = 4;

export type LayoutWidget = {
    id: string;
    size: WidgetSize;
    x?: number;
    y?: number;
};

export type PlacedWidget<T extends LayoutWidget = LayoutWidget> = T & { x: number; y: number };

export type RowBand = {
    y: number;
    top: number;
    bottom: number;
};

export type CellRect = {
    left: number;
    top: number;
    width: number;
    height: number;
};

export function widthForSize(size: WidgetSize): number {
    switch (size) {
        case 'large':
            return 12;
        case 'medium':
            return 8;
        case 'small':
            return 4;
        default:
            return 4;
    }
}

export function clampColumn(x: number, width: number): number {
    const max = Math.max(0, COLUMNS - width);
    const stepped = Math.round(x / COLUMN_STEP) * COLUMN_STEP;
    return Math.max(0, Math.min(max, stepped));
}

export function hasCoordinates<T extends LayoutWidget>(widget: T): widget is PlacedWidget<T> {
    return Number.isInteger(widget.x) && Number.isInteger(widget.y);
}

export function assignFlowCoordinates<T extends LayoutWidget>(widgets: T[]): PlacedWidget<T>[] {
    let x = 0;
    let y = 0;
    return widgets.map((widget) => {
        const width = widthForSize(widget.size);
        if (x + width > COLUMNS) {
            x = 0;
            y += 1;
        }
        const placed = { ...widget, x, y };
        x += width;
        if (x >= COLUMNS) {
            x = 0;
            y += 1;
        }
        return placed;
    });
}

export function ensureCoordinates<T extends LayoutWidget>(widgets: T[]): PlacedWidget<T>[] {
    if (widgets.length > 0 && widgets.every(hasCoordinates)) {
        return widgets.map((widget) => ({
            ...widget,
            x: clampColumn(widget.x, widthForSize(widget.size)),
            y: Math.max(0, widget.y),
        }));
    }
    return assignFlowCoordinates(widgets);
}

export function widgetsOverlap(a: PlacedWidget, b: PlacedWidget): boolean {
    if (a.id === b.id || a.y !== b.y) return false;
    const aWidth = widthForSize(a.size);
    const bWidth = widthForSize(b.size);
    return a.x < b.x + bWidth && b.x < a.x + aWidth;
}

export function resolveOverlaps<T extends LayoutWidget>(
    widgets: PlacedWidget<T>[],
    pinnedId: string
): PlacedWidget<T>[] {
    const result = widgets.map((widget) => ({ ...widget }));
    const pinned = result.find((widget) => widget.id === pinnedId);
    if (!pinned) return result;

    pinned.x = clampColumn(pinned.x, widthForSize(pinned.size));
    pinned.y = Math.max(0, pinned.y);

    const others = result
        .filter((widget) => widget.id !== pinnedId)
        .sort((a, b) => a.y - b.y || a.x - b.x);

    const placed: PlacedWidget<T>[] = [pinned];
    for (const widget of others) {
        widget.x = clampColumn(widget.x, widthForSize(widget.size));
        widget.y = Math.max(0, widget.y);
        while (placed.some((item) => widgetsOverlap(item, widget))) {
            widget.y += 1;
        }
        placed.push(widget);
    }
    return result;
}

export function placeWidget<T extends LayoutWidget>(
    widgets: T[],
    id: string,
    x: number,
    y: number
): PlacedWidget<T>[] {
    const laidOut = ensureCoordinates(widgets);
    const next = laidOut.map((widget) =>
        widget.id === id
            ? { ...widget, x: clampColumn(x, widthForSize(widget.size)), y: Math.max(0, Math.round(y)) }
            : { ...widget }
    );
    return resolveOverlaps(next, id);
}

export function compactRows<T extends LayoutWidget>(widgets: PlacedWidget<T>[]): PlacedWidget<T>[] {
    const used = [...new Set(widgets.map((widget) => widget.y))].sort((a, b) => a - b);
    const remap = new Map(used.map((row, index) => [row, index]));
    return widgets.map((widget) => ({ ...widget, y: remap.get(widget.y) ?? 0 }));
}

export function commitPlacement<T extends LayoutWidget>(
    widgets: T[],
    id: string,
    x: number,
    y: number
): PlacedWidget<T>[] {
    return compactRows(placeWidget(widgets, id, x, y));
}

export function findFirstFit(widgets: PlacedWidget[], width: number): { x: number; y: number } {
    const maxY = widgets.reduce((max, widget) => Math.max(max, widget.y), -1);
    for (let y = 0; y <= maxY + 1; y += 1) {
        for (let x = 0; x <= COLUMNS - width; x += COLUMN_STEP) {
            const candidate: PlacedWidget = {
                id: '__new__',
                size: width === 12 ? 'large' : width === 8 ? 'medium' : 'small',
                x,
                y,
            };
            if (!widgets.some((widget) => widgetsOverlap(widget, candidate))) {
                return { x, y };
            }
        }
    }
    return { x: 0, y: maxY + 1 };
}

export function applyResize<T extends LayoutWidget>(
    widgets: T[],
    id: string,
    size: WidgetSize
): PlacedWidget<T>[] {
    const next = ensureCoordinates(widgets).map((widget) =>
        widget.id === id
            ? { ...widget, size, x: clampColumn(widget.x, widthForSize(size)) }
            : { ...widget }
    );
    return compactRows(resolveOverlaps(next, id));
}

export function applyAdd<T extends LayoutWidget>(widgets: T[], widget: T): PlacedWidget<T>[] {
    const laidOut = ensureCoordinates(widgets);
    const { x, y } = findFirstFit(laidOut, widthForSize(widget.size));
    return compactRows([...laidOut, { ...widget, x, y }]);
}

export function applyRemove<T extends LayoutWidget>(widgets: T[], id: string): PlacedWidget<T>[] {
    return compactRows(ensureCoordinates(widgets).filter((widget) => widget.id !== id));
}

export function snapColumn(
    pointerX: number,
    gridLeft: number,
    gridWidth: number,
    width: number
): number {
    if (gridWidth <= 0) return 0;
    const ratio = (pointerX - gridLeft) / gridWidth;
    const raw = ratio * COLUMNS;
    const stepped = Math.floor(raw / COLUMN_STEP) * COLUMN_STEP;
    return clampColumn(stepped, width);
}

export function snapRow(pointerY: number, bands: RowBand[]): number {
    if (bands.length === 0) return 0;
    const sorted = [...bands].sort((a, b) => a.y - b.y);
    if (pointerY < sorted[0].top) return sorted[0].y;
    for (let i = 0; i < sorted.length; i += 1) {
        const current = sorted[i];
        const next = sorted[i + 1];
        const limit = next ? (current.bottom + next.top) / 2 : current.bottom;
        if (pointerY < limit) return current.y;
    }
    return sorted[sorted.length - 1].y + 1;
}

export function buildRowBands(
    widgets: Array<{ id: string; y: number }>,
    rects: Record<string, { top: number; bottom: number }>
): RowBand[] {
    const byY = new Map<number, { top: number; bottom: number }>();
    for (const widget of widgets) {
        const rect = rects[widget.id];
        if (!rect) continue;
        const existing = byY.get(widget.y);
        if (!existing) {
            byY.set(widget.y, { top: rect.top, bottom: rect.bottom });
        } else {
            byY.set(widget.y, {
                top: Math.min(existing.top, rect.top),
                bottom: Math.max(existing.bottom, rect.bottom),
            });
        }
    }
    return [...byY.entries()]
        .map(([y, band]) => ({ y, ...band }))
        .sort((a, b) => a.y - b.y);
}

export function columnWidth(gridWidth: number, gap: number): number {
    return (gridWidth - gap * (COLUMNS - 1)) / COLUMNS;
}

export function cellOriginX(x: number, colWidth: number, gap: number): number {
    return x * (colWidth + gap);
}

export function cellSpanWidth(span: number, colWidth: number, gap: number): number {
    return span * colWidth + Math.max(0, span - 1) * gap;
}

export function positionsForLayout(
    widgets: PlacedWidget[],
    colWidth: number,
    gap: number,
    heights: Record<string, number>
): Record<string, CellRect> {
    const maxY = widgets.reduce((max, widget) => Math.max(max, widget.y), 0);
    const rowHeight: number[] = Array.from({ length: maxY + 1 }, () => 0);
    for (const widget of widgets) {
        rowHeight[widget.y] = Math.max(rowHeight[widget.y], heights[widget.id] || 0);
    }
    const rowTop: number[] = [];
    let acc = 0;
    for (let y = 0; y <= maxY; y += 1) {
        rowTop[y] = acc;
        if (rowHeight[y] > 0) acc += rowHeight[y] + gap;
    }
    const positions: Record<string, CellRect> = {};
    for (const widget of widgets) {
        const span = widthForSize(widget.size);
        positions[widget.id] = {
            left: cellOriginX(widget.x, colWidth, gap),
            top: rowTop[widget.y] ?? 0,
            width: cellSpanWidth(span, colWidth, gap),
            height: heights[widget.id] || 0,
        };
    }
    return positions;
}

export function pointerInsideGrid(
    pointer: { x: number; y: number },
    grid: { left: number; right: number; top: number; bottom: number }
): boolean {
    return pointer.x >= grid.left && pointer.x <= grid.right && pointer.y >= grid.top - 8;
}
